import React, { useState, useEffect, useRef } from 'react';
import './index.css';

/**
 * 半圆形进度仪表盘组件
 * 根据分数动态渲染进度条和指针位置
 * 
 * @param {number} score - 分数值 (0-100)，决定指针位置和进度条颜色
 * @param {number} width - SVG宽度，默认300
 * @param {number} height - SVG高度，默认160
 * @param {boolean} animated - 是否启用动画，默认true
 * @param {number} animationDuration - 动画持续时间(ms)，默认1000
 * 
 * 规律分析：
 * - 仪表盘是半圆形，从顶部（-90度）到右侧（90度），总共180度
 * - 分数范围：0-100分
 * - 指针角度计算：角度 = (分数 / 100) * 180 - 90
 *   - 0分对应-90度（顶部）
 *   - 50分对应0度（中间）
 *   - 100分对应90度（右侧）
 * - 颜色分段：
 *   - 0-33分：红色区域 (#D33430)
 *   - 33-66分：黄色区域 (#F1930F)
 *   - 66-100分：绿色区域 (#27AE60)
 *   - 未填充部分：灰色 (#e7e7e7)
 */
const CircularGauge = ({
  loading = false,
  score = 0,
  width = 260,
  height = 120,
  animated = true,
  animationDuration = 300,
  c_redEndScore = 60,
  c_yellowStartScore = 61,
  c_yellowEndScore = 85,
  c_greenStartScore = 86,
}) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const animatedScoreRef = useRef(0);

  useEffect(() => {
    animatedScoreRef.current = animatedScore;
  }, [animatedScore]);

  useEffect(() => {
    if (!animated) {
      setAnimatedScore(score);
      animatedScoreRef.current = score;
      return;
    }

    let startTime = null;
    let animationFrameId = null;
    const startValue = animatedScoreRef.current;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / animationDuration, 1);

      // 使用缓动函数
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (score - startValue) * easeOutCubic;
      setAnimatedScore(currentValue);
      animatedScoreRef.current = currentValue;

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setAnimatedScore(score);
        animatedScoreRef.current = score;
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [score, animated, animationDuration]);

  // 限制分数范围在0-100
  const clampedScore = Math.max(0, Math.min(100, animatedScore));

  // 中心点（扇形区域的中心）
  const centerX = width / 2;
  const centerY = height - 10;

  // 缩放比例（基于原始尺寸300x160）
  const scaleX = width / 300;
  const scaleY = height / 160;
  // 使用统一的缩放比例（取较小值以保持比例）
  const scale = Math.min(scaleX, scaleY);

  // 计算指针角度和位置
  // 仪表盘是半圆形，从顶部(-90度)开始，到右侧(90度)结束
  // 0分对应-90度（顶部），50分对应0度（中间竖直），100分对应90度（右侧）
  const angleRange = 180; // 从-90度到90度，总共180度
  const pointerStartAngle = -90; // 0分对应的角度（顶部）
  const angleFromTop = pointerStartAngle + (clampedScore / 100) * angleRange;
  const angleRad = angleFromTop * (Math.PI / 180);

  // 指针旋转角度（用于SVG transform rotate）
  // rect初始是水平的（从左到右，0度方向）
  // 要让它指向angleFromTop方向，需要旋转angleFromTop度
  // 但如果50分应该竖直向上，而angleFromTop=0（水平向右），那么需要旋转-90度
  // 所以：pointerAngle = angleFromTop - 90
  // 这样：
  //   - 0分：angleFromTop=-90，pointerAngle=-180（水平向左）
  //   - 50分：angleFromTop=0，pointerAngle=-90（竖直向上，对！）
  //   - 100分：angleFromTop=90，pointerAngle=0（水平向右）
  const pointerAngle = angleFromTop - 90;

  // 计算旋转中心在圆弧上的位置
  // 指针旋转中心在半径约120的圆弧上（按比例缩放）
  const pointerRadius = 120 * scale;
  const pointerCenterX = centerX + pointerRadius * Math.sin(angleRad);
  const pointerCenterY = centerY - pointerRadius * Math.cos(angleRad);

  // 根据旋转中心计算rect的位置
  // rect的中心点 = 旋转中心
  // rect的左上角 = 旋转中心 - (25, 2.5)（按比例缩放）
  // const rectX = pointerCenterX - 25 * scale;
  // const rectY = pointerCenterY - 2.5 * scale;

  // 根据SVG尺寸进行缩放
  // const scaledRectX = rectX;
  // const scaledRectY = rectY;
  const scaledPointerCenterX = pointerCenterX;
  const scaledPointerCenterY = pointerCenterY;

  // 计算矩形的外端位置（相对于旋转中心）
  // 矩形宽度是 35 * scaleX，中心在旋转中心，所以右端距离中心是 35 * scaleX / 2
  const rectHalfWidth = (35 * scaleX) / 2;
  
  // 三角形路径的边界框中心点（根据路径数据估算）
  // 路径 x 范围: 0.697173 到 12.5382，中心约 6.6
  // 路径 y 范围: 0.183118 到 11.5013，中心约 5.8
  const triangleCenterX = 6.6;
  const triangleCenterY = 5.8;

  // 辅助函数：将角度转换为坐标点（在圆弧上，相对于中心）
  const angleToPoint = (angle, radius) => {
    const rad = angle * (Math.PI / 180);
    return {
      x: radius * Math.sin(rad),
      y: -radius * Math.cos(rad)
    };
  };

  // 计算填充区域的路径（从startAngle到endAngle）
  const getFillPath = (startAngle, endAngle, innerRadius, outerRadius) => {
    const startInner = angleToPoint(startAngle, innerRadius);
    const startOuter = angleToPoint(startAngle, outerRadius);
    const endInner = angleToPoint(endAngle, innerRadius);
    const endOuter = angleToPoint(endAngle, outerRadius);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${startInner.x} ${startInner.y} 
            L ${startOuter.x} ${startOuter.y} 
            A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}
            L ${endInner.x} ${endInner.y}
            A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${startInner.x} ${startInner.y}`;
  };

  // 根据分数区间计算路径分隔角度
  // 总角度范围：180度（半圆，从-90度到90度）
  // 分数到角度的映射：angle = -90 + (score / 100) * 180
  //   - 0分 → -90度（顶部）
  //   - 50分 → 0度（中间竖直）
  //   - 100分 → 90度（右侧）
  // 半径按比例缩放（基于原始尺寸300x160）
  const innerRadius = 100 * scale;
  const outerRadius = 140 * scale;
  const totalAngleRange = 180; // 半圆，从-90度到90度
  const startAngle = -90; // 半圆起始角度
  const gapAngle = 1; // 区域之间的间隔角度（度），总共2个间隔，占用2度

  // 计算各分数对应的角度（不考虑间隔）
  const scoreToAngle = (score) => startAngle + (score / 100) * totalAngleRange;

  // 计算各区间边界角度（考虑间隔）
  // 红色区域：0-60分
  const redEndScore = c_redEndScore;
  const redEndAngleRaw = scoreToAngle(redEndScore); // -90 + 108 = 18度
  const redEndAngle = redEndAngleRaw - gapAngle / 2; // 18 - 1 = 17度

  // 黄色区域：61-85分
  const yellowStartScore = c_yellowStartScore;
  const yellowStartAngleRaw = scoreToAngle(yellowStartScore); // -90 + 109.8 = 19.8度
  const yellowStartAngle = yellowStartAngleRaw + gapAngle / 2; // 19.8 + 1 = 20.8度
  const yellowEndScore = c_yellowEndScore;
  const yellowEndAngleRaw = scoreToAngle(yellowEndScore); // -90 + 153 = 63度
  const yellowEndAngle = yellowEndAngleRaw - gapAngle / 2; // 63 - 1 = 62度

  // 绿色区域：86-100分
  const greenStartScore = c_greenStartScore;
  const greenStartAngleRaw = scoreToAngle(greenStartScore); // -90 + 154.8 = 64.8度
  const greenStartAngle = greenStartAngleRaw + gapAngle / 2; // 64.8 + 1 = 65.8度
  const greenEndAngle = 90; // 限制在90度

  // 生成各区域的路径（灰色背景）
  const redPath = getFillPath(startAngle, redEndAngle, innerRadius, outerRadius);
  const yellowPath = getFillPath(yellowStartAngle, yellowEndAngle, innerRadius, outerRadius);
  const greenPath = getFillPath(greenStartAngle, Math.min(greenEndAngle, 90), innerRadius, outerRadius);

  // 根据分数计算填充路径
  // 直接根据分数计算对应的角度，确保填充路径和指针位置一致
  let redFillPath = null;
  let yellowFillPath = null;
  let greenFillPath = null;

  // 计算当前分数对应的角度
  const currentAngle = scoreToAngle(clampedScore);

  if (clampedScore <= c_redEndScore) {
    // 红色区域：0-60分
    const fillEndAngle = Math.min(currentAngle, redEndAngle);
    if (fillEndAngle > startAngle) {
      redFillPath = getFillPath(startAngle, fillEndAngle, innerRadius, outerRadius);
    }
  } else if (clampedScore <= c_yellowEndScore) {
    // 先填充完整的红色区域
    redFillPath = getFillPath(startAngle, redEndAngle, innerRadius, outerRadius);
    // 再填充黄色区域
    const fillEndAngle = Math.min(currentAngle, yellowEndAngle);
    if (fillEndAngle > yellowStartAngle) {
      yellowFillPath = getFillPath(yellowStartAngle, fillEndAngle, innerRadius, outerRadius);
    }
  } else {
    // 先填充完整的红色和黄色区域
    redFillPath = getFillPath(startAngle, redEndAngle, innerRadius, outerRadius);
    yellowFillPath = getFillPath(yellowStartAngle, yellowEndAngle, innerRadius, outerRadius);
    // 再填充绿色区域
    const fillEndAngle = Math.min(currentAngle, greenEndAngle);
    if (fillEndAngle > greenStartAngle) {
      greenFillPath = getFillPath(greenStartAngle, fillEndAngle, innerRadius, outerRadius);
    }
  }

  return (
    <div className="circular-gauge">
      <svg width={width} height={height} version="1.1" xmlns="http://www.w3.org/2000/svg">
        {/* 先绘制灰色背景 */}
        <g transform={`translate(${centerX}, ${centerY})`}>
          {/* 红色扇形区域（0-60分） */}
          <path
            d={redPath}
            fill={loading ? "#f6d6d6" : "#e7e7e7"}
            stroke="none"
          />

          {/* 黄色扇形区域（61-85分） */}
          <path
            d={yellowPath}
            fill={loading ? "#fce9cf" : "#e7e7e7"}
            stroke="none"
          />

          {/* 绿色扇形区域（86-100分） */}
          <path
            d={greenPath}
            fill={loading ? "#d4efdf" : "#e7e7e7"}
            stroke="none"
          />

          {/* 根据分数填充对应区域 */}
          {redFillPath && (
            <path
              d={redFillPath}
              fill="#D33430"
              stroke="none"
            />
          )}

          {yellowFillPath && (
            <path
              d={yellowFillPath}
              fill="#F1930F"
              stroke="none"
            />
          )}

          {greenFillPath && (
            <path
              d={greenFillPath}
              fill="#27AE60"
              stroke="none"
            />
          )}
        </g>

        {/* 指针 - 将三角形和矩形放在同一组中一起旋转 */}
        <g transform={`translate(${scaledPointerCenterX}, ${scaledPointerCenterY}) rotate(${pointerAngle})`}>
          {/* 矩形轴 - 从中心向右延伸 */}
          <rect
            x={-rectHalfWidth - 2}
            y={-2 * scaleY}
            width={29 * scaleX}
            height={4 * scaleY}
            rx={2 * scaleX}
            ry={2 * scaleY}
            fill="#303030"
            stroke="none"
          />
          {/* 三角形头部 - 位于矩形右端 */}
          <g transform={`rotate(35 18 5) translate(${rectHalfWidth}, 0) scale(${scale + 0.5})`}>
            <path 
              d="M2.23572 11.3018C0.888831 11.5013 -0.0571293 10.0101 0.697173 8.87655L5.85344 1.12979C6.48352 0.183118 7.8794 0.197873 8.48857 1.15812L12.5382 7.54152C13.147 8.50173 12.5658 9.77091 11.4411 9.93758L2.23572 11.3018Z" 
              fill="#303030" 
              stroke="white" 
              strokeWidth="0.857143"
              transform={`translate(-${triangleCenterX}, -${triangleCenterY})`}
            />
          </g>
        </g>

      </svg>
    </div>
  );
};

export default CircularGauge;
